import { useRef } from 'react';

import { cn } from '@rocco/utils/cn';
import {
    ClearGlyph,
    SearchGlyph,
} from '~/ui-rocco/pages/directory-browse/DirectoryPillBar';

interface ProfessionalTabSearchFieldProps {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    ariaLabel?: string;
    /** Width and placement — the field owns its height and ink. */
    className?: string;
}

/**
 * The listing tabs' search field: the product directory's bar field — the
 * glyph, the term, a clear — filtering as the reader types. One definition
 * for Products, Projects, Articles, Videos, Network and Downloads, so the
 * six tabs open on the same control.
 */
export const ProfessionalTabSearchField = ({
    value,
    onChange,
    placeholder,
    ariaLabel = 'Search this listing',
    className,
}: ProfessionalTabSearchFieldProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const hasTerm = value.length > 0;

    return (
        <form
            role="search"
            onSubmit={(event) => {
                event.preventDefault();
                inputRef.current?.blur();
            }}
            className={cn(
                'flex h-[2.75rem] items-center gap-2 rounded-[1rem] border px-[1.3125rem] text-[#323232] transition-colors',
                // A term in the field is applied state: the listing's flat fill.
                hasTerm
                    ? 'border-transparent bg-[#F2F2F2]'
                    : 'border-[#141414]/20',
                'focus-within:border-[#141414]/50 focus-within:bg-transparent',
                className,
            )}
        >
            <span className="text-[#767676]">
                <SearchGlyph />
            </span>
            <input
                ref={inputRef}
                type="text"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                placeholder={placeholder}
                aria-label={ariaLabel}
                className="min-w-0 flex-1 border-none bg-transparent text-[1rem] tracking-[-0.02rem] text-[#323232] outline-none placeholder:text-[#767676]"
            />
            {hasTerm ? (
                <button
                    type="button"
                    onClick={() => onChange('')}
                    aria-label="Clear search"
                    className="flex shrink-0 cursor-pointer items-center justify-center bg-transparent p-0 text-[#767676] transition-colors hover:text-[#323232]"
                >
                    <ClearGlyph />
                </button>
            ) : null}
        </form>
    );
};
